import { KPI, KPIMonthlyValue } from "../api/fetchMemberDashboard/types";
import { KPIConfig } from "../config/kpiConfig";
import { DashboardKPI, KPIUnit } from "../types";

/** Pairs the API's numbers with the config that formats and explains them. */

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-07` → `Jul`. Parsed rather than dated, to avoid a timezone shifting the month. */
const shortMonth = (yearMonth: string): string => {
  const index = Number(yearMonth.slice(5, 7)) - 1;

  return MONTH_LABELS[index] ?? yearMonth;
};

/** `2026-07` → `Jul 2026`. */
const longMonth = (yearMonth: string): string =>
  `${shortMonth(yearMonth)} ${yearMonth.slice(0, 4)}`;

export const formatValue = (value: number, unit: KPIUnit): string => {
  switch (unit) {
    case "percent":
      return `${value.toFixed(1)}%`;
    case "days":
      return `${Math.round(value)} days`;
    case "ratio":
      return value.toFixed(2);
    case "currency":
    default:
      return formatCurrency(value);
  }
};

const formatCurrency = (value: number): string => {
  const magnitude = Math.abs(value);

  if (magnitude >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }

  if (magnitude >= 10_000) {
    return `$${Math.round(value / 1_000)}K`;
  }

  return `$${Math.round(value).toLocaleString("en-US")}`;
};

const formatDelta = (value: number, unit: KPIUnit): string => {
  const magnitude = Math.abs(value);

  switch (unit) {
    case "percent":
      return `${magnitude.toFixed(1)} pts`;
    case "days":
      return `${Math.round(magnitude)} days`;
    case "ratio":
      return magnitude.toFixed(2);
    case "currency":
    default:
      return formatCurrency(magnitude);
  }
};

/**
 * The numerator with the member's own figures substituted in.
 *
 * A margin's numerator is what is *left* of revenue once costs come out, so
 * printing it as a single figure hides where it came from — "$107K" appears
 * nowhere in the member's QuickBooks and matches nothing on the card. Those
 * KPIs show the subtraction their formula describes: "($262K − $155K)".
 *
 * The deduction is derived rather than sent: the numerator is the remainder of
 * the denominator, so what came out is the difference between them.
 */
const numeratorFigure = (
  numerator: number,
  denominator: number,
  config: KPIConfig,
): string =>
  config.numeratorIsRemainder
    ? `(${formatCurrency(denominator)} − ${formatCurrency(denominator - numerator)})`
    : formatCurrency(numerator);

/**
 * "$79K ÷ $249K" — one month's own arithmetic, for the trend chart's hover.
 *
 * The multiplier and the result are left off: the tooltip shows the ratio on
 * the line above, so repeating it here would say the same thing twice.
 */
const working = (point: KPIMonthlyValue, config: KPIConfig): string =>
  `${numeratorFigure(point.numerator, point.denominator, config)} ÷ ${formatCurrency(point.denominator)}`;

/** "$318 ÷ $1,000 × 100 = 31.8%" — the headline month's own arithmetic. */
const workedExample = (kpi: KPI, config: KPIConfig): string | null => {
  if (
    kpi.numerator === null ||
    kpi.denominator === null ||
    kpi.value === null
  ) {
    return null;
  }

  const numerator = numeratorFigure(kpi.numerator, kpi.denominator, config);
  const denominator = formatCurrency(kpi.denominator);
  const result = formatValue(kpi.value, config.unit);
  const multiplier = config.multiplier ? ` ${config.multiplier}` : "";

  return `${numerator} ÷ ${denominator}${multiplier} = ${result}`;
};

export const toDashboardKPI = (kpi: KPI, config: KPIConfig): DashboardKPI => {
  const delta = kpi.yearOverYearDelta;
  const isUp = (delta ?? 0) > 0;

  return {
    code: kpi.code,
    name: config.name,
    source: config.source,
    definition: config.definition,
    guidance: config.guidance,
    valueLabel: kpi.value === null ? null : formatValue(kpi.value, config.unit),
    monthLabel: kpi.yearMonth === null ? null : longMonth(kpi.yearMonth),
    deltaLabel:
      delta === null || kpi.comparedToYearMonth === null
        ? null
        : `${formatDelta(delta, config.unit)} vs ${longMonth(kpi.comparedToYearMonth)}`,
    deltaIsGood:
      delta === null
        ? true
        : config.direction === "lower_is_better"
          ? delta <= 0
          : delta >= 0,
    deltaIsUp: isUp,
    chart: kpi.monthlyValues.map((point) => ({
      label: shortMonth(point.yearMonth),
      value: point.value,
      monthLabel: longMonth(point.yearMonth),
      valueLabel:
        point.value === null ? null : formatValue(point.value, config.unit),
      workingLabel: point.value === null ? null : working(point, config),
    })),
    formula: {
      numerator: config.numeratorLabel,
      denominator: config.denominatorLabel,
      multiplier: config.multiplier,
      workedExample: workedExample(kpi, config),
      note: config.note,
    },
    isIncomplete: kpi.isIncomplete,
  };
};
